import { FormattedMessage } from 'react-intl';

import { isTG2orTG3 } from '@/components/Asset';
import { EngineOilPreConditionsAndFeedback } from '@/features/diagnostics/sidebar/EngineOilPreConditionsAndFeedback';
import { Asset, AssetType, ComponentItems, Criticality, DiagnosticsMeasurementOverview, FillLevelMeasurements, FillLevelType } from '@/generated/fleetAggregationServiceTypes';
import { getIconClassesForCriticality } from '@/utils/criticalityMappings';
import { isEngineOilTypeDid, isEngineOilTypeV2 } from '@/utils/engineOilUtils';
import { OverviewTable } from './OverviewTable';
import SolvedByCustomer from './SolvedByCustomer';

type ItemType = ComponentItems | FillLevelMeasurements;

type SidebarDataLimitedProps = {
    items: ItemType[];
    overview: DiagnosticsMeasurementOverview;
    asset: Asset;
    isReadingNotPossible?: boolean;
};

const ListItem = (item: ItemType, asset: Asset, isReadingNotPossible: boolean, index: number) => {
    const showExtraInformationBanner = item.type === FillLevelType.EngineOil && (isTG2orTG3(asset) || asset.type === AssetType.Bus);
    const showEngineOilReadingPreconditions = isEngineOilTypeDid(item.type) || isEngineOilTypeV2(item.type);
    const dynamicEngineOilReadingPreconditions = showEngineOilReadingPreconditions ? (item as FillLevelMeasurements)?.engine_oil_preconditions : undefined;
    const sideColor = isReadingNotPossible ? '' : `list-group-item-${item.criticality}`;
    const style = isReadingNotPossible ? { boxShadow: 'inset 5px 0 0 0 var(--s)' } : undefined;

    const getMessageIdForItemName = ({ type, criticality }: ItemType): string => {
        if (item.type === FillLevelType.EngineOil) {
            return `intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.criticality.${item.criticality}`;
        } else if (item.type === FillLevelType.EngineOilDid || item.type === FillLevelType.EngineOilV2) {
            const engineOilState = item.level_engine_oil_state;
            return `intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.${engineOilState}`;
        } else {
            // If is not any kind of engine oil, then we want to show the name of the component
            return `intl-msg:fleetstatus.diagnostics.sidebar.section.fill_levels.type.${item.type}`;
        }
    };

    const isComponentItems = (x: ItemType): x is ComponentItems => (x as ComponentItems).name !== undefined;

    return (
        <div key={index} className={`list-group-item ${sideColor} rounded-none display-flex align-items-center justify-content-between`} style={style}>
            {isComponentItems(item) ? (
                <>
                    <div className="text-color-darkest ellipsis-1" title={item.name}>
                        <span className={`${getIconClassesForCriticality(item.criticality, 'placeholder')} margin-right-10`} />
                        <span>{item.name}</span>
                    </div>

                    {item.solvedByCustomer && item.type === 'maintenance_component' && (
                        <div className="margin-left-10">
                            <SolvedByCustomer />
                        </div>
                    )}
                </>
            ) : (
                <div className="text-color-darkest">
                    <div className={`${showExtraInformationBanner ? 'flex-basis-30pct' : ''}`}>
                        <span className={`${getIconClassesForCriticality(item.criticality, 'placeholder')} margin-right-10`} />
                        <FormattedMessage id={getMessageIdForItemName(item)} />
                    </div>

                    {showExtraInformationBanner && (
                        <div className="flex-basis-70pct">
                            <span className="rioglyph rioglyph-dangerousgoods margin-right-5" />
                            <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.section.fill_levels.data_not_accurate" />
                        </div>
                    )}
                </div>
            )}

            {showEngineOilReadingPreconditions && (
                <EngineOilPreConditionsAndFeedback
                    engineOilType={item.type as FillLevelType}
                    engineOilPreconditions={dynamicEngineOilReadingPreconditions}
                    product={asset.product}
                />
            )}
        </div>
    );
};

export const SidebarDataLimited = ({ items, overview, asset, isReadingNotPossible = false }: SidebarDataLimitedProps): JSX.Element => {
    const itemsToRender = items.filter(({ criticality }) => criticality === Criticality.Warning || criticality === Criticality.Danger);

    if (!overview.warning && !overview.danger) {
        const itemFiltered = items.find(({ type }) => isEngineOilTypeDid(type) || isEngineOilTypeV2(type));

        return <OverviewTable overview={overview} engineOil={itemFiltered as FillLevelMeasurements} product={asset.product} />;
    }

    return (
        <div className="list-group margin-bottom-25">
            {itemsToRender.map((item, index) => ListItem(item, asset, isReadingNotPossible, index))}

            {!!overview.unknown && (
                <div className="list-group-item rounded-none display-flex align-items-center">
                    <div className="text-color-gray text-lowercase">
                        <span className={`${getIconClassesForCriticality(Criticality.Unknown, 'doNotShow')} margin-right-10`} />
                        <span className="text-bold">{overview.unknown !== undefined && `${overview.unknown} `}</span>
                        <FormattedMessage id="intl-msg:fleetstatus.diagnostics.criticality.unknown" />
                    </div>
                </div>
            )}
        </div>
    );
};
